<template>
  <!-- Pillola della barra globale (Angelo, 4/11/2026): a riposo mostra solo la scelta attiva, col fondo terracotta; col mouse sopra (o col focus da tastiera)
       si allarga e scopre le altre scelte, e il fondo scorre sotto quella toccata. Su telefono, senza mouse, resta chiusa e un tocco passa alla scelta
       successiva. Larghezze fisse per segmento, quindi solo CSS e nessuna misura. -->
  <div
    role="radiogroup"
    :aria-label="ariaLabel"
    class="group/toggle box-content inline-flex h-8 w-(--w) shrink-0 overflow-hidden rounded-full bg-glass-strong p-0.5 shadow-[inset_0_1px_3px_rgb(0_0_0/0.2),0_0_0_1px_color-mix(in_srgb,var(--color-graphite)_12%,transparent)] transition-[width] duration-(--motion-fluid) ease-(--ease-fluid) has-focus-visible:w-(--larghezza-aperta) [@media(hover:hover)]:hover:w-(--larghezza-aperta)"
    :style="{ '--w': larghezza, '--n': options.length, '--i': indice, '--larghezza-aperta': `calc(${larghezza} * ${options.length})`, '--sposta': `calc(${larghezza} * ${-indice})` }"
  >
    <div class="relative flex h-full translate-x-(--sposta) transition-transform duration-(--motion-fluid) ease-(--ease-fluid) group-has-focus-visible/toggle:translate-x-0 [@media(hover:hover)]:group-hover/toggle:translate-x-0">
      <span
        class="pointer-events-none absolute inset-y-0 left-0 rounded-full bg-accent transition-transform duration-(--motion-fluid) ease-(--ease-fluid)"
        :style="{ width: larghezza, transform: `translateX(${indice * 100}%)` }"
        aria-hidden="true"
        data-test="toggle-fondo"
      />
      <button
        v-for="(opzione, i) in options"
        :key="opzione.value"
        :ref="(el) => (bottoni[i] = el as HTMLButtonElement | null)"
        type="button"
        role="radio"
        :aria-checked="i === indice ? 'true' : 'false'"
        :aria-label="opzione.ariaLabel"
        :tabindex="i === indice ? 0 : -1"
        class="focusable relative z-10 inline-flex h-full shrink-0 cursor-pointer select-none items-center justify-center rounded-full text-xs font-semibold transition-colors duration-(--motion-slow)"
        :class="i === indice ? 'text-on-accent' : 'text-text-secondary enabled:hover:text-text-primary'"
        :style="{ width: larghezza }"
        @click="scegli(i)"
        @keydown="onKeydown($event, i)"
      >
        <i v-if="opzione.icon" :class="[opzione.icon, 'block leading-none icon-hover-pop']" aria-hidden="true" />
        <span v-else class="leading-none">{{ opzione.label }}</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts" generic="T extends string | number">
// Scelta tra poche opzioni per la barra globale (lingua, tema): stesso contratto di SegmentedControl (gruppo di radio, frecce, Home/End), ma in forma di
// pillola che a riposo mostra solo l'opzione attiva. Il tocco sull'opzione già attiva, dove non c'è il mouse, passa alla successiva.
import type { OpzioneSegmento } from "~/types/ui";

const props = withDefaults(defineProps<{ options: OpzioneSegmento<T>[]; ariaLabel: string; larghezza?: string }>(), { larghezza: "2.25rem" });
const model = defineModel<T | null>({ default: null });
const bottoni = ref<Array<HTMLButtonElement | null>>([]);
const indice = computed(() => Math.max(0, props.options.findIndex((o) => o.value === model.value)));

function scegli(i: number): void {
  const opzione = props.options[i];
  if (!opzione) return;
  // Pillola chiusa (nessun mouse): il tocco sull'opzione attiva passa alla successiva
  const senzaMouse = typeof window !== "undefined" && !window.matchMedia("(hover: hover)").matches;
  const destinazione = i === indice.value && senzaMouse ? props.options[(i + 1) % props.options.length] : opzione;
  if (destinazione) model.value = destinazione.value;
}

function onKeydown(evento: KeyboardEvent, i: number): void {
  const destinazione = destinazioneDaTasto(evento.key, i, props.options.length, () => true);
  if (destinazione === undefined) return;
  evento.preventDefault();
  const opzione = props.options[destinazione];
  if (opzione) model.value = opzione.value;
  bottoni.value[destinazione]?.focus();
}
</script>
